// LeadAssigneeWinRatesReport.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { startOfYear } from 'date-fns';
import { normalizeDateValue, timestampFromDate } from 'utilities/DateTime';
import {
  LEAD_ASSIGNEE_WIN_RATES_SORT,
  buildLeadAssigneeWinRatesRows,
  formatAvgDurationDays,
  formatPercent,
} from 'collection/reports/lead/leadAssigneeWinRates';

// CSS
import './LeadAssigneeWinRatesReport.css';

// Icons
import Icon from 'svg/Icon';

// Components
import NodeDateInput from 'node/form/fields/date/NodeDateInput';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import SelectInput from 'components/selectinput/SelectInput';

const DATE_FRIENDLY_FORMAT = Object.freeze({
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const SORT_OPTIONS = [
  { value: LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON, label: '% Won' },
  { value: LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_LOST, label: '% Lost' },
  { value: LEAD_ASSIGNEE_WIN_RATES_SORT.ASSIGNED, label: 'Assigned' },
  { value: LEAD_ASSIGNEE_WIN_RATES_SORT.AVG_DURATION, label: 'Avg days' },
  { value: LEAD_ASSIGNEE_WIN_RATES_SORT.NAME, label: 'Assignee' },
];

const COLUMNS = [
  {
    key: LEAD_ASSIGNEE_WIN_RATES_SORT.NAME,
    label: 'Assignee',
    sortable: true,
    className: 'is-assignee',
  },
  {
    key: LEAD_ASSIGNEE_WIN_RATES_SORT.ASSIGNED,
    label: 'Assigned',
    sortable: true,
    className: 'is-metric',
    getValue: (row) => row.assigned,
  },
  {
    key: 'won',
    label: 'Won',
    sortable: false,
    className: 'is-metric',
    getValue: (row) => row.won,
  },
  {
    key: 'lost',
    label: 'Lost',
    sortable: false,
    className: 'is-metric',
    getValue: (row) => row.lost,
  },
  {
    key: LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON,
    label: '% Won',
    sortable: true,
    className: 'is-metric',
    getValue: (row) => formatPercent(row.percentWon),
  },
  {
    key: LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_LOST,
    label: '% Lost',
    sortable: true,
    className: 'is-metric',
    getValue: (row) => formatPercent(row.percentLost),
  },
  {
    key: LEAD_ASSIGNEE_WIN_RATES_SORT.AVG_DURATION,
    label: 'Avg days',
    sortable: true,
    className: 'is-metric',
    getValue: (row) => formatAvgDurationDays(row.avgDurationDays),
  },
];

const toDateFieldValue = (date) => {
  if (!date) return null;
  return {
    startDate: timestampFromDate(date),
    startPrecision: 'date',
    endDate: null,
    endPrecision: null,
    isRange: false,
  };
};

const fromDateFieldValue = (value) => {
  if (!value?.startDate) return null;
  return normalizeDateValue(value.startDate);
};

/**
 * LeadAssigneeWinRatesReport
 *
 * Collection Overview widget: closed Lead win/loss rates by assignee.
 *
 * @param {object} props
 * @param {object[]} props.nodes - Raw collection nodes (not view-filtered).
 * @param {boolean} [props.isLoading]
 * @param {string} [props.title]
 * @param {string} [props.description]
 *
 * @returns {ReactNode}
 */
const LeadAssigneeWinRatesReport = ({
  nodes = [],
  isLoading = false,
  title = 'Assignee Win Rates',
  description = 'Closed leads by assignee: counts, win/loss mix, avg cycle time.',
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [rangeStart, setRangeStart] = useState(() => startOfYear(new Date()));
  const [rangeEnd, setRangeEnd] = useState(() => new Date());
  const [sortKey, setSortKey] = useState(LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON);
  const [sortDirection, setSortDirection] = useState('desc');

  // ----- MEMOS ----- //

  const rows = useMemo(() => (
    buildLeadAssigneeWinRatesRows({
      nodes,
      rangeStart,
      rangeEnd,
      sortKey,
      sortDirection,
    })
  ), [nodes, rangeStart, rangeEnd, sortKey, sortDirection]);

  const summaryText = useMemo(() => {
    if (isLoading || !rows.length) return null;
    const closed = rows.reduce((sum, row) => sum + (row.assigned || 0), 0);
    const assigneeLabel = rows.length === 1 ? 'assignee' : 'assignees';
    const leadLabel = closed === 1 ? 'closed lead' : 'closed leads';
    return `${closed} ${leadLabel} · ${rows.length} ${assigneeLabel}`;
  }, [isLoading, rows]);

  // ----- HANDLERS ----- //

  /**
   * handleSortKeyChange
   *
   * Updates the sort column and picks a sensible default direction.
   *
   * @param {object} event
   */
  const handleSortKeyChange = (event) => {
    const nextKey = event?.target?.value || LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON;
    setSortKey(nextKey);
    setSortDirection(
      nextKey === LEAD_ASSIGNEE_WIN_RATES_SORT.NAME ? 'asc' : 'desc',
    );
  };

  /**
   * handleSortDirectionToggle
   *
   * Flips ascending / descending for the active sort column.
   */
  const handleSortDirectionToggle = () => {
    setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
  };

  /**
   * handleSortColumnClick
   *
   * Toggles direction when re-clicking the active column; otherwise selects
   * the column with a sensible default direction.
   *
   * @param {string} columnKey
   */
  const handleSortColumnClick = (columnKey) => {
    if (!columnKey) return;

    if (columnKey === sortKey) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(columnKey);
    setSortDirection(
      columnKey === LEAD_ASSIGNEE_WIN_RATES_SORT.NAME ? 'asc' : 'desc',
    );
  };

  const sortDirectionLabel = sortDirection === 'asc'
    ? 'Low to high'
    : 'High to low';

  // ----- RENDER ----- //

  return (
    <div
      className="lead-assignee-win-rates-report-container"
      style={{
        color: theme.foreground,
      }}
    >
      {/* INTRO */}
      <div className="lead-assignee-win-rates-report-intro">
        <div className="lead-assignee-win-rates-report-title">
          {title}
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="lead-assignee-win-rates-report-toolbar">
        <div className="lead-assignee-win-rates-report-toolbar-group">
          <div className="lead-assignee-win-rates-report-date-row">
            <div className="lead-assignee-win-rates-report-date-control">
              <NodeDateInput
                className="lead-assignee-win-rates-report-date-input"
                friendlyFormatOptions={DATE_FRIENDLY_FORMAT}
                iconSize={18}
                value={toDateFieldValue(rangeStart)}
                onChange={(next) => setRangeStart(fromDateFieldValue(next))}
              />
            </div>
            <span
              className="lead-assignee-win-rates-report-date-separator"
              style={{ color: theme.foregroundMuted }}
            >
              to
            </span>
            <div className="lead-assignee-win-rates-report-date-control">
              <NodeDateInput
                className="lead-assignee-win-rates-report-date-input"
                friendlyFormatOptions={DATE_FRIENDLY_FORMAT}
                iconSize={18}
                value={toDateFieldValue(rangeEnd)}
                onChange={(next) => setRangeEnd(fromDateFieldValue(next))}
              />
            </div>
          </div>
        </div>

        {/* SORT 
        <div className="lead-assignee-win-rates-report-toolbar-group">
          <div
            className="lead-assignee-win-rates-report-filter-label"
            style={{ color: theme.foregroundMuted }}
          >
            Sort by
          </div>
          <div className="lead-assignee-win-rates-report-sort-row">
            <div className="lead-assignee-win-rates-report-sort-select">
              <SelectInput
                options={SORT_OPTIONS}
                showIcons={false}
                value={sortKey}
                height="50px"
                width="100%"
                onChange={handleSortKeyChange}
              />
            </div>
            <button
              aria-label={`Sort direction: ${sortDirectionLabel}`}
              className="lead-assignee-win-rates-report-sort-direction"
              title={sortDirectionLabel}
              type="button"
              onClick={handleSortDirectionToggle}
            >
              <Icon
                color="currentColor"
                height="18px"
                iconKey={
                  sortDirection === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                }
                width="18px"
              />
              <span>{sortDirectionLabel}</span>
            </button>
          </div>
        </div>
        */}
      </div>

      {/* BODY */}
      {isLoading ? (
        <div
          className="lead-assignee-win-rates-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Loading leads…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="lead-assignee-win-rates-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          No closed assigned leads with an inquiry date in this range.
        </div>
      ) : (
        <div className="lead-assignee-win-rates-report-body">

          {/* GRID */}
          <div className="lead-assignee-win-rates-report-grid">
            {/* COLUMN HEADERS */}
            <div className="lead-assignee-win-rates-report-grid-row is-header">
              {COLUMNS.map((column) => {
                const isActive = column.sortable && column.key === sortKey;

                if (!column.sortable) {
                  return (
                    <div
                      key={column.key}
                      className={`lead-assignee-win-rates-report-cell ${column.className}`}
                    >
                      {column.label}
                    </div>
                  );
                }

                return (
                  <button
                    key={column.key}
                    aria-sort={
                      isActive
                        ? (sortDirection === 'asc' ? 'ascending' : 'descending')
                        : 'none'
                    }
                    className={`lead-assignee-win-rates-report-cell ${column.className} is-sortable`}
                    type="button"
                    onClick={() => handleSortColumnClick(column.key)}
                  >
                    <span>{column.label}</span>
                    {isActive ? (
                      <Icon
                        color="currentColor"
                        height="14px"
                        iconKey={
                          sortDirection === 'asc'
                            ? 'keyboard_arrow_up'
                            : 'keyboard_arrow_down'
                        }
                        width="14px"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {rows.map((row) => (
              <div
                key={row.assigneeKey}
                className="lead-assignee-win-rates-report-grid-row"
              >
                {COLUMNS.map((column) => {
                  if (column.key === LEAD_ASSIGNEE_WIN_RATES_SORT.NAME) {
                    return (
                      <div
                        key={`${row.assigneeKey}-${column.key}`}
                        className={`lead-assignee-win-rates-report-cell ${column.className}`}
                      >
                        <ProfilePhoto
                          borderWidth={0}
                          node={{
                            key: row.assigneeKey,
                            userKey: row.assigneeKey,
                            name: row.assigneeName,
                            coverUrl: row.coverUrl,
                          }}
                          size={28}
                        />
                        <span className="lead-assignee-win-rates-report-assignee-name">
                          {row.assigneeName}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`${row.assigneeKey}-${column.key}`}
                      className={`lead-assignee-win-rates-report-cell ${column.className}`}
                    >
                      {column.getValue(row)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Dev Tools
LeadAssigneeWinRatesReport.displayName = 'LeadAssigneeWinRatesReport';

export default LeadAssigneeWinRatesReport;
